import { useTranslations } from "use-intl";

import { AlbumGrid } from "@/components/album-grid";
import { TrackingScope } from "@/components/TrackingScope";
import { Button } from "@/components/ui/button";
import type { FeaturePageRenderModel } from "@garimpa/api/featurePages/types";

import { CollectionAnnotationBadge } from "./collection-annotation-badge";
import { CollectionAnnotationNote } from "./collection-annotation-note";
import type { CollectionGridItem } from "./collection-grid-item";
import { CollectionRankingTag } from "./collection-ranking-tag";
import type { FeaturePageAlbumGridItem } from "./feature-page-album-grid-item";

type CollectionModel = NonNullable<FeaturePageRenderModel["collection"]>;
type CollectionAlbum = FeaturePageAlbumGridItem & { collectionItem: CollectionGridItem };

export function CollectionGridResults({
  albums,
  collection,
  currentPage,
  onClearFilters,
  onPageChange,
  pageCount,
}: {
  albums: CollectionAlbum[];
  collection: CollectionModel;
  currentPage: number;
  onClearFilters: () => void;
  onPageChange: (page: number) => void;
  pageCount: number;
}) {
  const t = useTranslations("featureCollections");

  return (
    <>
      {albums.length > 0 ? (
        <TrackingScope entityType={collection.definition.itemEntityType} surface="feature_page">
          <AlbumGrid
            albums={albums}
            className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6"
            itemKey={(album) => album.collectionItem.member.catalogRefId}
            link={(album) => album.link}
            linkCoversFooter
            priorityCount={2}
            showAlbumType={false}
            showOfferSummary={false}
            tags={(album) => (
              <>
                <CollectionRankingTag position={album.collectionItem.member.position} />
                <CollectionAnnotationBadge
                  badge={album.collectionItem.member.annotation?.badge ?? null}
                />
              </>
            )}
            footer={(album) => (
              <CollectionAnnotationNote
                note={album.collectionItem.member.annotation?.note ?? null}
              />
            )}
          />
        </TrackingScope>
      ) : (
        <div className="mt-6 rounded-2xl border border-dashed border-border px-5 py-12 text-center">
          <p className="text-sm text-muted-foreground">{t("grid.noResults")}</p>
          <Button className="mt-4" onClick={onClearFilters} size="sm" variant="outline">
            {t("grid.clearFilters")}
          </Button>
        </div>
      )}

      {pageCount > 1 ? (
        <div className="mt-8 grid grid-cols-2 gap-3 sm:flex sm:items-center sm:justify-center">
          <Button
            className="col-start-1 row-start-2 w-full sm:w-auto"
            disabled={currentPage === 1}
            onClick={() => onPageChange(currentPage - 1)}
            size="sm"
            variant="outline"
          >
            {t("grid.previousPage")}
          </Button>
          <span
            className="col-span-2 row-start-1 min-w-28 text-center text-sm text-muted-foreground"
            aria-live="polite"
          >
            {t("grid.pageLabel", { current: currentPage, total: pageCount })}
          </span>
          <Button
            className="col-start-2 row-start-2 w-full sm:w-auto"
            disabled={currentPage === pageCount}
            onClick={() => onPageChange(currentPage + 1)}
            size="sm"
            variant="outline"
          >
            {t("grid.nextPage")}
          </Button>
        </div>
      ) : null}
    </>
  );
}
